import { recordingDownloadHref, useGlobalPlayer } from "@bandwith/recordings";
import { formatDuration } from "@bandwith/time";
import { Button, MediaItemContent } from "@bandwith/ui";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Download, GripVertical, Pause, Play, X } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { RouterLinkWrapper } from "./RouterLinkWrapper";

/**
 * Normalized item type for the sortable media list.
 * Both recordings and songs are transformed to this shape.
 */
export type MediaItem = {
  id: string;
  title: string;
  subtitle?: string | null;
  linkHref?: string;
  isPrimary?: boolean;
  /** Hidden from anonymous visitors; shown with an "Unlisted" subtitle tag */
  unlisted?: boolean;
  /** Title type: "title" (default, display font) or "filename" (mono font) */
  titleType?: "title" | "filename";
  audio?: {
    recordingId: string;
    filename: string;
    signedUrl: string;
    songId: string;
    songTitle: string;
    authorName: string | null;
    durationSeconds: number;
  } | null;
};

type Props = {
  items: MediaItem[];
  isEditing: boolean;
  disabled?: boolean;
  onReorder?: (newOrder: string[]) => void;
  onDelete?: (id: string) => void;
  /** Extra per-item controls rendered before the delete button in edit mode */
  renderEditControls?: (item: MediaItem) => React.ReactNode;
  emptyState?: React.ReactNode;
  testIdPrefix?: string;
};

function useDndSensors() {
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(TouchSensor, { activationConstraint: { distance: 8 } }),
  );
}

function getItemClasses(
  isDragging: boolean,
  isCurrentTrack: boolean,
  isPrimary: boolean,
  isHighlighted: boolean,
): string {
  const base =
    "flex items-center gap-3 rounded-lg border bg-bg-secondary p-4 transition-colors select-none";
  const highlight = isHighlighted ? " animate-highlight-new" : "";

  let variant: string;
  if (isDragging) variant = "z-10 border-amber bg-amber/10 shadow-lg";
  else if (isCurrentTrack) variant = "border-amber bg-amber/5";
  else if (isPrimary) variant = "border-amber/50 bg-amber/5";
  else variant = "border-border-subtle hover:bg-bg-tertiary";

  return `${base} ${variant}${highlight}`;
}

type SortableItemProps = {
  item: MediaItem;
  isEditing: boolean;
  disabled?: boolean;
  onDelete?: (id: string) => void;
  renderEditControls?: (item: MediaItem) => React.ReactNode;
  isCurrentTrack: boolean;
  isThisPlaying: boolean;
  isHighlighted: boolean;
  onPlayPause: () => void;
  testIdPrefix: string;
};

function SortableItem({
  item,
  isEditing,
  disabled,
  onDelete,
  renderEditControls,
  isCurrentTrack,
  isThisPlaying,
  isHighlighted,
  onPlayPause,
  testIdPrefix,
}: SortableItemProps) {
  const {
    setNodeRef,
    transform,
    transition,
    isDragging,
    attributes,
    listeners,
  } = useSortable({ id: item.id, disabled: !isEditing || !!disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const leftContent = isEditing ? (
    <button
      type="button"
      data-testid={`${testIdPrefix}-drag-handle`}
      className="flex h-11 w-11 cursor-grab items-center justify-center rounded-lg text-text-muted transition-colors touch-none hover:bg-bg-tertiary active:cursor-grabbing active:bg-bg-tertiary disabled:opacity-50"
      disabled={disabled}
      {...attributes}
      {...listeners}
    >
      <GripVertical className="h-5 w-5" aria-hidden="true" />
    </button>
  ) : item.audio ? (
    <Button
      variant={isCurrentTrack ? "player-active" : "player"}
      size="icon"
      onClick={onPlayPause}
      className={isThisPlaying ? "animate-pulse-ring" : ""}
      aria-label={isThisPlaying ? "Pause" : "Play"}
    >
      {isThisPlaying ? (
        <Pause className="h-4 w-4" aria-hidden="true" />
      ) : (
        <Play className="ml-0.5 h-4 w-4" aria-hidden="true" />
      )}
    </Button>
  ) : (
    <div className="h-10 w-10" />
  );

  let rightContent: React.ReactNode;
  if (isEditing) {
    const editControls = renderEditControls?.(item);
    const deleteButton = onDelete ? (
      <button
        type="button"
        onClick={() => onDelete(item.id)}
        disabled={disabled}
        className="flex h-11 w-11 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-bg-tertiary hover:text-red-muted disabled:opacity-30"
        aria-label={`Remove ${item.title}`}
      >
        <X className="h-5 w-5" aria-hidden="true" />
      </button>
    ) : undefined;
    rightContent =
      editControls || deleteButton ? (
        <div className="flex items-center gap-1">
          {editControls}
          {deleteButton}
        </div>
      ) : undefined;
  } else if (item.audio) {
    rightContent = (
      <a
        href={recordingDownloadHref(item.audio.recordingId)}
        download={item.audio.filename}
        data-testid={`${testIdPrefix}-download`}
        className="flex h-11 w-11 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-bg-tertiary hover:text-text-primary"
        aria-label={`Download ${item.audio.filename}`}
      >
        <Download className="h-5 w-5" aria-hidden="true" />
      </a>
    );
  }

  const subtitleParts: string[] = [];
  if (item.unlisted) subtitleParts.push("Unlisted");
  if (item.subtitle) subtitleParts.push(item.subtitle);
  if (item.audio?.durationSeconds) {
    subtitleParts.push(formatDuration(item.audio.durationSeconds));
  }
  const subtitle = subtitleParts.length > 0 ? subtitleParts.join(" · ") : null;

  return (
    <li
      ref={setNodeRef}
      style={style}
      data-testid={`${testIdPrefix}-item`}
      className={getItemClasses(
        isDragging,
        isCurrentTrack,
        !!item.isPrimary,
        isHighlighted,
      )}
    >
      <MediaItemContent
        title={item.title}
        subtitle={subtitle}
        linkHref={isEditing ? undefined : item.linkHref}
        leftContent={leftContent}
        rightContent={rightContent}
        titleType={item.titleType}
        LinkWrapper={RouterLinkWrapper}
        data-testid={`${testIdPrefix}-title`}
      />
    </li>
  );
}

export function SortableMediaList({
  items,
  isEditing,
  disabled,
  onReorder,
  onDelete,
  renderEditControls,
  emptyState,
  testIdPrefix = "media",
}: Props) {
  const sensors = useDndSensors();
  const { play, pause, resume, isPlaying, currentTrackIndex, tracks } =
    useGlobalPlayer((s) => ({
      play: s.play,
      pause: s.pause,
      resume: s.resume,
      isPlaying: s.isPlaying,
      currentTrackIndex: s.currentTrackIndex,
      tracks: s.tracks,
    }));

  const seenIds = useRef<Set<string> | null>(null);
  if (seenIds.current === null) {
    seenIds.current = new Set(items.map((i) => i.id));
  }
  const itemIds = useMemo(() => items.map((i) => i.id), [items]);

  useEffect(() => {
    for (const id of itemIds) {
      seenIds.current?.add(id);
    }
  }, [itemIds]);

  const currentTrack =
    currentTrackIndex !== null ? tracks[currentTrackIndex] : null;

  const globalTracks = useMemo(() => {
    return items
      .filter(
        (
          item,
        ): item is MediaItem & { audio: NonNullable<MediaItem["audio"]> } =>
          item.audio != null,
      )
      .map((item) => ({
        id: item.audio.recordingId,
        filename: item.audio.filename,
        signedAudioUrl: item.audio.signedUrl,
        songId: item.audio.songId,
        songTitle: item.audio.songTitle,
        authorName: item.audio.authorName,
      }));
  }, [items]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id && onReorder) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      const newOrder = items.map((item) => item.id);
      const movedItem = newOrder.splice(oldIndex, 1)[0];
      if (movedItem !== undefined) {
        newOrder.splice(newIndex, 0, movedItem);
      }
      onReorder(newOrder);
    }
  };

  if (items.length === 0) {
    return emptyState ?? null;
  }

  const listContent = items.map((item) => {
    const isCurrentTrackMatch = item.audio
      ? currentTrack?.id === item.audio.recordingId
      : false;
    const isThisPlaying = isCurrentTrackMatch && isPlaying;
    const isHighlighted = seenIds.current
      ? !seenIds.current.has(item.id)
      : false;

    const handlePlayPause = () => {
      if (!item.audio) return;
      if (isCurrentTrackMatch && isPlaying) {
        pause();
      } else if (isCurrentTrackMatch) {
        resume();
      } else {
        const trackIndex = globalTracks.findIndex(
          (t) => t.id === item.audio?.recordingId,
        );
        if (trackIndex !== -1) {
          play(globalTracks, trackIndex, false);
        }
      }
    };

    return (
      <SortableItem
        key={item.id}
        item={item}
        isEditing={isEditing}
        disabled={disabled}
        onDelete={onDelete}
        renderEditControls={renderEditControls}
        isCurrentTrack={isCurrentTrackMatch}
        isThisPlaying={isThisPlaying}
        isHighlighted={isHighlighted}
        onPlayPause={handlePlayPause}
        testIdPrefix={testIdPrefix}
      />
    );
  });

  if (!isEditing) {
    return <ul className="space-y-3">{listContent}</ul>;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map((item) => item.id)}
        strategy={verticalListSortingStrategy}
      >
        <ul className="space-y-3">{listContent}</ul>
      </SortableContext>
    </DndContext>
  );
}
